import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * `HR-ORG01b`: web's `Table`, the primitive rule's list of rows (`docs/03`
 * §6), drawn as the canvas's tables are: a header row on `neutral-50` with
 * plain titles (never upper-cased or letter-spaced, so a Bangla title reads as
 * written, `docs/03` §4), hairline rows, and the whole table scrolling sideways
 * inside its own box on a narrow screen instead of the page.
 */
export const Table = React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>(function Table(
  { className, ...props },
  ref,
) {
  // `relative`: a `sr-only` cell label is `position: absolute`, and with no
  // positioned ancestor it would escape this scroll box and widen the page on a
  // phone (the `AS-FIX04` trap); here it is contained and scrolls with the table.
  return (
    <div className="relative w-full overflow-x-auto">
      <table ref={ref} className={cn("w-full border-collapse text-sm", className)} {...props} />
    </div>
  );
});

export const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  function TableHeader({ className, ...props }, ref) {
    return <thead ref={ref} className={cn("bg-neutral-50", className)} {...props} />;
  },
);

export const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  function TableBody({ className, ...props }, ref) {
    return <tbody ref={ref} className={cn("[&>tr:last-child]:border-0", className)} {...props} />;
  },
);

export const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(function TableRow(
  { className, ...props },
  ref,
) {
  return <tr ref={ref} className={cn("border-b border-neutral-100", className)} {...props} />;
});

export const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  function TableHead({ className, ...props }, ref) {
    return (
      <th
        ref={ref}
        scope="col"
        className={cn(
          "whitespace-nowrap border-b border-neutral-200 px-4 py-2.5 text-left text-xs font-semibold text-neutral-500",
          className,
        )}
        {...props}
      />
    );
  },
);

export const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  function TableCell({ className, ...props }, ref) {
    return <td ref={ref} className={cn("px-4 py-3 align-middle", className)} {...props} />;
  },
);
